import React from 'react';
import { useTranslation } from 'react-i18next';

const formatNum = (n) => {
  if (!n && n !== 0) return '—';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/,/g, ' ');
};

const formatUSD = (n) => {
  if (!n && n !== 0) return '$0.00';
  return '$' + (parseFloat(n) || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SkeletonCard = () => (
  <div
    className="rounded-xl p-5 animate-pulse"
    style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
  >
    <div className="h-3 w-24 rounded mb-3" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-7 w-32 rounded mb-2" style={{ backgroundColor: '#1f1f1f' }} />
    <div className="h-2 w-16 rounded" style={{ backgroundColor: '#1f1f1f' }} />
  </div>
);

const KPICard = ({ label, value, sub, accent }) => (
  <div
    className="rounded-xl p-5 flex flex-col gap-1"
    style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
  >
    <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
      {label}
    </p>
    <p className="text-2xl font-bold" style={{ color: accent || '#fff', fontFamily: 'Sora, sans-serif' }}>
      {value}
    </p>
    {sub && (
      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
        {sub}
      </p>
    )}
  </div>
);

const PerformancesKPICards = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6]?.map(i => <SkeletonCard key={i} />)}
      </div>
    );
  }

  const { totalDiamonds = 0, totalBonus = 0, totalCharge = 0, activeCreators = 0, netAgence = 0, avgDiamonds = 0 } = data || {};

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <KPICard
        label={t('admin.performance.totalDiamonds', 'Total Diamonds')}
        value={formatNum(totalDiamonds)}
        sub={t('admin.performance.sumDiamonds', 'SUM(diamonds)')}
        accent="#f59e0b"
      />
      <KPICard
        label={t('admin.performance.totalBonus', 'Total Bonus')}
        value={formatUSD(totalBonus)}
        sub={t('admin.performance.sumTotalBonus', 'SUM(total_bonus)')}
        accent="#22c55e"
      />
      <KPICard
        label={t('admin.performance.totalCharge', 'Total Charge')}
        value={formatUSD(totalCharge)}
        sub={t('admin.performance.sumCharge', 'SUM(charge)')}
        accent="#ef4444"
      />
      <KPICard
        label={t('admin.performance.activeCreators', 'Créateurs actifs')}
        value={activeCreators?.toLocaleString('fr-FR')}
        sub={t('admin.performance.diamondsGt0', 'diamonds > 0')}
        accent="#3b82f6"
      />
      <KPICard
        label={t('admin.performance.netAgency', 'Net Agence')}
        value={formatUSD(netAgence)}
        sub={t('admin.performance.sumNetAgency', 'SUM(net_agency)')}
        accent="#a855f7"
      />
      <KPICard
        label={t('admin.performance.avgDiamonds', 'Moy. Diamonds')}
        value={formatNum(Math.round(avgDiamonds))}
        sub={t('admin.performance.perActiveCreator', 'Par créateur actif')}
        accent="#f97316"
      />
    </div>
  );
};

export default PerformancesKPICards;
